import { useEffect, useState } from 'react';
import { X, Download, Share } from 'lucide-react';

const DISMISS_KEY = 'zita_pwa_dismissed_at';
const DISMISS_WINDOW_MS = 1000 * 60 * 60 * 24 * 14; // 14 days between prompts

/**
 * Branded in-app PWA install prompt.
 *
 * Wraps the browser's `beforeinstallprompt` event so we can present the
 * install invitation in Zita's own visual language (instead of a generic
 * browser bar). For platforms that don't fire the event — most notably
 * iOS Safari — we show platform-specific "Add to Home Screen" instructions.
 *
 * Dismissal is remembered for 14 days so we don't nag returning users.
 */
export const PWAInstallPrompt = () => {
  const [deferred, setDeferred] = useState(null);   // beforeinstallprompt event
  const [visible, setVisible] = useState(false);
  const [platform, setPlatform] = useState('generic'); // 'android' | 'ios' | 'desktop' | 'generic'

  useEffect(() => {
    // Suppress if user dismissed recently, or if already running standalone.
    const dismissedAt = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (Date.now() - dismissedAt < DISMISS_WINDOW_MS) return;

    const isStandalone =
      window.matchMedia?.('(display-mode: standalone)').matches ||
      window.navigator.standalone === true;
    if (isStandalone) return;

    const ua = navigator.userAgent || '';
    const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    const isAndroid = /Android/.test(ua);
    const isDesktop = !isIOS && !isAndroid && !/Mobile/.test(ua);

    const onBIP = (e) => {
      e.preventDefault();
      setDeferred(e);
      setPlatform(isDesktop ? 'desktop' : isAndroid ? 'android' : 'generic');
      // Delay 6s so users can read the page before the invitation appears.
      setTimeout(() => setVisible(true), 6000);
    };
    window.addEventListener('beforeinstallprompt', onBIP);

    // iOS never fires beforeinstallprompt — we show manual instructions.
    let iosTimer = null;
    if (isIOS) {
      setPlatform('ios');
      iosTimer = setTimeout(() => setVisible(true), 8000);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', onBIP);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome === 'accepted') {
      // Success — hide permanently for this browser
      localStorage.setItem(DISMISS_KEY, String(Date.now() + DISMISS_WINDOW_MS * 10));
    } else {
      dismiss();
    }
    setDeferred(null);
    setVisible(false);
  };

  if (!visible) return null;

  const showsBrowserPrompt = platform === 'android' || platform === 'desktop';
  const isIOS = platform === 'ios';

  return (
    <div
      className="fixed inset-x-3 bottom-3 lg:inset-x-auto lg:right-6 lg:bottom-6 lg:max-w-sm z-50"
      role="dialog"
      aria-label="Install Zita"
      data-testid="pwa-install-prompt"
    >
      <div
        className="rounded-2xl border shadow-2xl overflow-hidden"
        style={{
          background: 'hsl(var(--zita-fog))',
          borderColor: 'hsl(var(--zita-hairline))',
          boxShadow: '0 24px 48px -12px rgba(20,32,58,0.24)',
        }}
      >
        <div className="p-5">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={{ background: 'hsl(var(--zita-orange-soft))' }}
              >
                <Download size={18} style={{ color: 'hsl(var(--zita-orange))' }} />
              </div>
              <div>
                <div className="font-serif text-lg" style={{ color: 'hsl(var(--zita-ink))' }}>
                  Install Zita
                </div>
                <div className="text-xs" style={{ color: 'hsl(var(--zita-ink-muted))' }}>
                  Faster access · works offline · looks like a real app
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={dismiss}
              aria-label="Dismiss"
              data-testid="pwa-install-dismiss"
              className="p-1 rounded hover:bg-black/5 shrink-0"
              style={{ color: 'hsl(var(--zita-ink-muted))' }}
            >
              <X size={16} />
            </button>
          </div>

          {isIOS && (
            <div
              className="mt-4 text-sm space-y-1"
              style={{ color: 'hsl(var(--zita-ink-soft))' }}
            >
              <div className="font-semibold">To install on iPhone / iPad:</div>
              <div className="flex items-start gap-2">
                <Share size={14} className="mt-0.5 shrink-0" />
                <span>
                  Tap the <strong>Share</strong> icon in Safari.
                </span>
              </div>
              <div>
                Then choose <strong>“Add to Home Screen”</strong>.
              </div>
            </div>
          )}

          {platform === 'generic' && !showsBrowserPrompt && !isIOS && (
            <div
              className="mt-4 text-sm"
              style={{ color: 'hsl(var(--zita-ink-soft))' }}
            >
              Open the browser menu and choose <strong>“Install app”</strong> or{' '}
              <strong>“Add to Home Screen”</strong>.
            </div>
          )}

          {showsBrowserPrompt && (
            <button
              type="button"
              onClick={install}
              data-testid="pwa-install-cta"
              className="mt-4 w-full zita-btn-primary justify-center"
            >
              Install Zita
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PWAInstallPrompt;
